// « Aujourd'hui, c'est l'anniversaire de Sandrine » — la carte du fil.
//
// ─────────────────────────────────────────────────────────────────────────────
// POURQUOI CE FICHIER EXISTE
//
// La date de naissance était demandée à l'inscription — étape 2 — et n'avait
// JAMAIS servi à rien depuis le premier jour. Elle dormait en base. Josaphat a
// choisi de s'en servir le 19 août 2026.
//
// ⚠️ ON NE LIT NI L'ÂGE NI LA DATE. La fiche publique ne porte que `neLe` —
// « 08-19 », le jour et le mois. La date complète est restée dans le dossier
// privé, où l'audit du 17 août l'a mise avec le téléphone et le genre. On peut
// souhaiter un anniversaire sans savoir quel âge on souhaite.
//
// ⚠️ ET SEULEMENT SES AMIS. Un anniversaire est une occasion de se parler, pas
// un annuaire à parcourir : voir passer celui d'un inconnu n'apprend rien et
// expose tout le monde.
//
// ⚠️ LE CALCUL A LIEU UNE FOIS PAR JOUR, PAS UNE FOIS PAR VISITE.
// Il faut lire la fiche de chaque ami pour savoir qui est né aujourd'hui. Fait à
// chaque ouverture du fil, ce serait cinquante lectures par session pour une
// carte vide trois cent soixante jours sur trois cent soixante-cinq. Le résultat
// est donc gardé dans le navigateur, sous la date du jour : le lendemain, la
// clé ne correspond plus et le calcul se refait tout seul. C'est le même
// raisonnement que l'annuaire du téléphone, relu une fois par jour.

import { useEffect, useState } from "react";
import Store from "../store.js";
import Avatar from "./Avatar.jsx";

/* « 08-19 » pour le 19 août. La même forme que `jourEtMois` dans le magasin —
   et, comme lui, sans fuseau horaire : l'anniversaire tombe le jour que le
   calendrier affiche, à Parakou comme ailleurs. */
function aujourdHui() {
  const d = new Date();
  return `${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/* ⚠️ LA CLÉ PORTE LE COMPTE — 20 octobre 2026. Elle était la même pour tout
   le monde (`kn.anniversaires`) : sur un téléphone partagé, le compte suivant
   héritait de la carte du précédent — les anniversaires des amis d'un AUTRE,
   avec leurs noms, leurs photos et le bouton « Souhaiter » ; ou aucune carte
   du tout si le cache de l'autre était vide ce jour-là. Trouvé à l'audit.
   `decouverte.js` faisait déjà bien (`kn.accueil.${uid}`) : on fait pareil. */
const cleDe = (uid) => `kn.anniversaires.${uid}`;

function garde(uid, jour) {
  try {
    const g = JSON.parse(localStorage.getItem(cleDe(uid)) || "null");
    return g && g.jour === jour ? g.gens : null;
  } catch {
    return null; // illisible : on recalcule, ce n'est pas une panne
  }
}

export default function Anniversaires({ ctx, relations }) {
  const [gens, setGens] = useState([]);

  useEffect(() => {
    const jour = aujourdHui();
    const connu = garde(ctx.moi.uid, jour);
    if (connu) return setGens(connu);

    const amis = (relations || [])
      .filter((r) => r.etat === "acceptee")
      .map((r) => (r.membres || []).find((m) => m !== ctx.moi.uid))
      .filter(Boolean);
    if (!amis.length) return;

    let vivant = true;
    (async () => {
      const fiches = await Promise.all(
        amis.map((u) => (ctx.profil ? ctx.profil(u).catch(() => null) : null))
      );
      if (!vivant) return;
      const nes = fiches.filter(
        (u) =>
          u &&
          u.neLe === jour &&
          /* Un membre peut éteindre son anniversaire dans ses paramètres.
             L'absence du bloc vaut « allumé » : c'est le défaut, et une fiche
             ancienne ne doit pas être traitée comme un refus. */
          u.confidentialite?.anniversaire !== false
      );
      const legers = nes.map((u) => ({
        uid: u.uid, nom: u.nom || "Membre", photo: u.photo || "",
      }));
      try {
        localStorage.setItem(cleDe(ctx.moi.uid), JSON.stringify({ jour, gens: legers }));
      } catch {
        // Mémoire pleine ou navigation privée : on affiche quand même.
      }
      setGens(legers);
    })();
    return () => { vivant = false; };
  }, [relations, ctx]);

  if (!gens.length) return null;

  return (
    <section className="carte anniversaires">
      <h2 className="anniversaires__titre">🎂 Anniversaires</h2>
      {gens.map((u) => (
        <div className="anniversaires__ligne" key={u.uid}>
          <a href={`#/profil/${u.uid}`}>
            <Avatar membre={u} taille={44} />
          </a>
          <p className="anniversaires__phrase">
            C'est l'anniversaire de{" "}
            <a className="anniversaires__nom" href={`#/profil/${u.uid}`}>{u.nom}</a>{" "}
            aujourd'hui.
          </p>
          {/* ⚠️ ON OUVRE LA CONVERSATION, ON N'ÉCRIT PAS À LA PLACE DES GENS.
              Un « Joyeux anniversaire ! » envoyé d'un bouton ne vaut rien pour
              celui qui le reçoit, et cela se voit. On amène jusqu'au champ de
              saisie ; le mot reste de vous. */}
          {/* ⚠️ LA ROUTE ATTEND UNE CONVERSATION, PAS UN MEMBRE.
              `convos.ensure` rend celle qui existe, ou la crée. C'est le même
              geste que le bouton « Message » d'un profil. */}
          <button
            type="button"
            className="btn btn--primaire btn--petit"
            onClick={async () => {
              try {
                location.hash = `#/messages/${await Store.convos.ensure(u.uid)}`;
              } catch {
                location.hash = `#/profil/${u.uid}`; // au pire, on ouvre le profil
              }
            }}
          >
            Souhaiter
          </button>
        </div>
      ))}
    </section>
  );
}
